"use client";

import clsx from "clsx";
import React from "react";
import {useTranslation} from "react-i18next";
import {detectFileType} from "@/utils/fileTypeDetection";
import {useObjectUrl, useExcelFile} from "@/hooks/useFilePreview";
import Icon from "../library/Icon";
import FileAttachmentThumbnail from "./FileAttachmentThumbnail";

interface FileAttachmentProps {
   file: File;
   onRemove: () => void;
   className?: string;
   isIncompatible?: boolean;
   incompatibilityReason?: string;
}

const FileAttachment: React.FC<FileAttachmentProps> = ({
   file,
   onRemove,
   className,
   isIncompatible = false,
   incompatibilityReason,
}) => {
   const {t} = useTranslation("ask");
   const fileType = detectFileType(file.type);
   const objectUrl = useObjectUrl(file, fileType.isPdf || fileType.isImage || fileType.isDocx);
   const excelData = useExcelFile(file, fileType.isExcel);
   const extension = file.name.split(".").pop()?.toUpperCase() ?? "FILE";

   return (
      <div
         className={clsx(
            "relative group rounded-xl overflow-hidden border transition-colors duration-200",
            "w-[140px] flex-shrink-0",
            isIncompatible
               ? "border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900"
               : "border-300 bg-100",
            className
         )}
         title={isIncompatible ? incompatibilityReason : undefined}
      >
         <button
            type="button"
            onClick={(e) => {
               e.stopPropagation();
               onRemove();
            }}
            className={clsx(
               "absolute top-1.5 right-1.5 z-10",
               "w-6 h-6 rounded-full bg-black/50 hover:bg-black/70",
               "flex items-center justify-center transition-colors",
               "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
            )}
            aria-label={t("a11y.removeFile")}
         >
            <Icon icon="xmark" className="text-white text-[10px]" />
         </button>

         <div className="h-[100px] flex items-center justify-center overflow-hidden bg-200/50">
            <FileAttachmentThumbnail fileType={fileType} objectUrl={objectUrl} excelData={excelData} file={file} />
         </div>

         <div className="absolute bottom-[34px] left-2">
            <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-black/40 text-white backdrop-blur-sm">
               {extension}
            </span>
         </div>

         <div className="px-2 py-1.5 border-t border-300">
            <p className="text-800 text-[11px] font-medium truncate">{file.name}</p>
         </div>
      </div>
   );
};

export default FileAttachment;
